/**
 * DirectSaleQrCard (Jun-2026, User-Wunsch) — sichtbare QR-Karte oben rechts neben
 * dem Titel der Rechnungsseite (analog zur Werkstatt-„Auftragsübersicht").
 *
 * Der QR zeigt auf `/dealer/invoices?action=direct-sale&scan=1`: mit dem Handy
 * gescannt öffnet er direkt den Direktverkauf samt Kamera-EAN-Scan. Klick auf die
 * Karte vergrößert den QR und bietet Druck als Aushang ODER auf Etikettenbögen
 * (Aug-2026, User-Wunsch: Etikettengröße wählbar).
 */
import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Printer, X } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { getApiOrgBranch } from "../../lib/api";
import { openQrPrint, dealerPrintInfo } from "../../lib/qrPrint";
import { QrLabelSheetButton } from "../QrLabelSheetButton";

export default function DirectSaleQrCard() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    // Jul-2026 (User-Wunsch): Der QR trägt den aktiven Standort mit — der per
    // Scan gestartete Direktverkauf landet am richtigen Standort (nicht am Hauptsitz).
    const [branch, setBranch] = useState(getApiOrgBranch());
    useEffect(() => {
        const h = (e) => setBranch(e.detail?.id || null);
        window.addEventListener("tw-branch-changed", h);
        return () => window.removeEventListener("tw-branch-changed", h);
    }, []);
    const url = `${window.location.origin}/dealer/invoices?action=direct-sale&scan=1${branch ? `&branch=${branch}` : ""}`;

    const handlePrint = () => openQrPrint({
        title: "Direktverkauf per Handy starten",
        subtitle: "Mit der Handy-Kamera scannen — öffnet den Direktverkauf und startet sofort den EAN-Scan.",
        scanUrl: url,
        ...dealerPrintInfo(user),
    });

    return (
        <div className="flex flex-col items-center gap-1.5" data-testid="direct-sale-qr">
            <div className="relative">
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    title="Mit dem Handy scannen → Direktverkauf mit EAN-Scan starten (Klick: vergrößern & drucken)"
                    className="bg-white rounded-md p-2.5 hover:ring-2 hover:ring-[#FFCC00] transition-shadow shadow-lg"
                    data-testid="direct-sale-qr-trigger"
                >
                    <QRCodeSVG value={url} size={108} level="M" data-testid="direct-sale-qr-code" />
                </button>
                {open && (
                    <div className="absolute right-0 top-[120px] z-50 w-80 bg-white border border-gray-200 rounded-sm shadow-xl p-4 text-[#111827]" data-testid="direct-sale-qr-panel">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold uppercase tracking-wider text-gray-700">Direktverkauf per Handy</span>
                            <button type="button" onClick={() => setOpen(false)} data-testid="direct-sale-qr-close"><X className="w-4 h-4 text-gray-500" /></button>
                        </div>
                        <div className="flex justify-center bg-white p-2 rounded-sm border-2 border-[#FFCC00]">
                            <QRCodeSVG value={url} size={180} level="M" />
                        </div>
                        <p className="text-[11px] text-gray-600 mt-2">Mit dem Handy scannen: öffnet den Direktverkauf und startet sofort den Kamera-EAN-Scan.</p>
                        <button type="button" onClick={handlePrint}
                            className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-[#111827] text-white rounded-sm py-2 text-xs font-bold uppercase tracking-wider hover:bg-black"
                            data-testid="direct-sale-qr-print">
                            <Printer className="w-4 h-4" /> QR-Code drucken (Aushang)
                        </button>
                        <QrLabelSheetButton scanUrl={url}
                            label="Direktverkauf per Handy — QR scannen"
                            testidPrefix="direct-sale-qr-label" />
                    </div>
                )}
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-300 flex items-center gap-1">
                <Smartphone className="w-3 h-3" /> Handy-Direktverkauf
            </span>
        </div>
    );
}
